/**
 * Copyright 2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import download from "downloadjs";
import { Button } from "@nordcloud/gnui";
import { useGetEventActionReportsLazyQuery } from "~/generated/graphql";
import { showError, showSuccess } from "~/services/toast";
import { generateActionSuccessText, isNotNil } from "~/tools";
import { downloadButtonState } from "~/views/events/EventsDetails/Tabs/Details/components/utils";

type Props = {
  eventId: string;
  eventActionId: string;
  resourceId: string;
};

export function ResourceDetailsActionDetailsSidebarHeaderDownload({
  eventId,
  eventActionId,
  resourceId,
}: Props) {
  const [getReport, { data: isData, loading, error: isError }] =
    useGetEventActionReportsLazyQuery({
      onCompleted: (data) => {
        const fileName = `${eventId}_${eventActionId}_${resourceId}_report`;
        if (data?.eventActionReports) {
          const reports = data?.eventActionReports?.map((eventActionReport) => {
            return {
              ...eventActionReport,
              report: {
                ...eventActionReport?.report,
                resources: eventActionReport?.report?.resources?.filter(
                  (resource) => resource.resourceId === resourceId
                ),
              },
            };
          });
          download(JSON.stringify(reports), fileName, "application/json");
        }
        showSuccess(generateActionSuccessText(fileName)()("downloaded")());
      },
      onError: (error) => {
        showError(error.message);
      },
    });

  const handleGetReport = () =>
    getReport({ variables: { eventActionId, eventId } });

  return (
    <Button
      severity="low"
      disabled={loading}
      icon="download"
      initialState={downloadButtonState({
        isData: isNotNil(isData),
        loading,
        isError: isNotNil(isError),
      })}
      onClick={handleGetReport}
    >
      Download JSON
    </Button>
  );
}
